import sounds from 'src/lib/sounds';
import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import { user } from 'src/StateObserver';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import * as Inventory from 'src/replicant/inventory';

import { AssetGroup } from '@play-co/timestep-core';

type RowData = {
  label: string;
  value: () => number;
};

const rows: RowData[] = [
  { label: 'paidGems', value: () => Inventory.getters.getPaidGems(user()) },
  { label: 'freeGems', value: () => Inventory.getters.getFreeGems(user()) },
  { label: 'total', value: () => Inventory.getters.getGems(user()) },
];

export default class PopupSettingsPurchaseInfo extends PopupBasic {
  private content: FlexBoxView;
  private valueLabels: LangBitmapFontTextView[] = [];

  constructor(opts: { superview: View; close: () => void }) {
    const height = 485;

    super({
      ...opts,
      height,
      tracking: {
        feature: 'settings',
        $subFeature: 'popup-settings-purchase-information',
      },
    });

    this.content = new FlexBoxView({
      superview: this.box,
      width: this.box.style.width,
      height,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 80,
      },
    });

    // Create individual rows for paid, free, and total gems
    rows.forEach(({ label }) => this.createInfoRow(label));

    // Create back CTA button
    this.createBackButton(opts.close);
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  private createInfoRow(label: String) {
    const { width } = this.content.style;

    const baseTextOpts: Partial<LangBitmapOpts> = {
      size: 30,
      height: 30,
      font: 'Body',
      wordWrap: false,
    };

    const flexContainer = new FlexBoxView({
      superview: this.content,
      width,
      height: 85,
      flex: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
    });

    const row = new FlexBoxView({
      superview: flexContainer,
      width,
      height: 73,
      flex: {
        paddingLeft: 25,
        paddingRight: 38,
        alignItems: 'center',
        justifyContent: 'space-between',
      },
    });

    // Add row label
    new LangBitmapFontTextView({
      ...baseTextOpts,
      superview: row,
      align: 'left',
      color: '#BF8147',
      localeText: () => i18n(`settings.purchaseInfo.${label}`),
    });

    // Add row value
    this.valueLabels.push(
      new LangBitmapFontTextView({
        ...baseTextOpts,
        superview: row,
        align: 'right',
        color: '#9A7171',
      }),
    );

    // Add row separator
    new View({
      superview: flexContainer,
      width: width - 12,
      height: 5,
      backgroundColor: '#FDC381',
    });
  }

  private createBackButton(close: () => void) {
    this.buttonClose.removeFromSuperview();

    const flexContainer = new FlexBoxView({
      superview: this.content,
      width: this.content.style.width,
      height: 135,
      flex: {
        alignItems: 'center',
        justifyContent: 'center',
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: flexContainer,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 235,
      height: 90,
      localeText: () => i18n('basic.back'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return close();
      },
      tracking: {
        feature: 'settings',
        $subFeature: 'popup-settings-purchase-information-back',
      },
    });
  }

  public init() {
    super.init({
      title: i18n('settings.purchaseInfo.title'),
    });

    // Update value labels with current gem amounts
    this.valueLabels.forEach((label, index) => {
      const { value } = rows[index];
      label.updateOpts({
        localeText: () => parseAmount(value()),
      });
    });
  }
}
